import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaPlus, FaSyncAlt, FaTrash } from 'react-icons/fa';
import { deleteAdminSport, getAdminSports, updateAdminSport } from '../../service/adminService';
import SmallLoadingAlt from '../global/smallLoadingAlt';
import globalStyles from '../globals.module.css';
import componentStyles from './adminPage.module.css';

const DEFAULT_SPORT_OPTIONS = ['NFL', 'NBA', 'MLB', 'NHL', 'MMA', 'SOCCER'];

const formatDateTime = (isoStr) => {
    if (!isoStr) return '-';
    const date = new Date(isoStr);
    return Number.isNaN(date.getTime()) ? isoStr : date.toLocaleString();
};

const normalizeSport = (sport) => String(sport || '').trim().toUpperCase();

const AdminSports = ({ refreshToken = 0 }) => {
    const [sports, setSports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingSport, setSavingSport] = useState('');
    const [deletingSportId, setDeletingSportId] = useState('');
    const [deleteCandidate, setDeleteCandidate] = useState(null);
    const [newSport, setNewSport] = useState('');
    const [error, setError] = useState('');
    const [status, setStatus] = useState('');

    const knownSportCodes = useMemo(() => new Set(sports.map((sport) => normalizeSport(sport.sport))), [sports]);
    const availableQuickAdds = DEFAULT_SPORT_OPTIONS.filter((sport) => !knownSportCodes.has(sport));

    const fetchSports = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await getAdminSports();
            const data = response.data || {};
            if (!data.success) {
                setError(data.error || 'Unable to load admin sports.');
                setSports([]);
                return;
            }
            setSports(Array.isArray(data.sports) ? data.sports : []);
        } catch (err) {
            if (err.response?.status === 401) {
                setError('Admin key rejected. Check the key and refresh.');
            } else {
                setError(err.response?.data?.error || err.message || 'Unable to load admin sports.');
            }
            setSports([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSports();
    }, [fetchSports, refreshToken]);

    const applySportUpdate = async (sportCode, isActive) => {
        const normalizedSport = normalizeSport(sportCode);
        if (!normalizedSport) return;

        setSavingSport(normalizedSport);
        setError('');
        setStatus('');
        try {
            const response = await updateAdminSport(normalizedSport, Boolean(isActive));
            const data = response.data || {};
            if (!data.success || !data.sport) {
                setError(data.error || `Unable to update ${normalizedSport}.`);
                return;
            }

            setSports((prevSports) => {
                const nextSport = data.sport;
                const existingIndex = prevSports.findIndex((sport) => sport._id === nextSport._id);
                if (existingIndex === -1) {
                    return [...prevSports, nextSport].sort((a, b) => normalizeSport(a.sport).localeCompare(normalizeSport(b.sport)));
                }

                return prevSports.map((sport, index) => index === existingIndex ? nextSport : sport);
            });
            setStatus(`${normalizedSport} ${isActive ? 'activated' : 'deactivated'}.`);
            setNewSport('');
        } catch (err) {
            if (err.response?.status === 401) {
                setError('Admin key rejected. Check the key and try again.');
            } else {
                setError(err.response?.data?.error || err.message || `Unable to update ${normalizedSport}.`);
            }
        } finally {
            setSavingSport('');
        }
    };

    const handleAddSport = (event) => {
        event.preventDefault();
        applySportUpdate(newSport, true);
    };

    const openDeleteConfirmation = (sport) => {
        const sportId = sport?._id;
        const sportCode = normalizeSport(sport?.sport);

        if (!sportId) {
            setError(`Unable to delete ${sportCode || 'sport'} because the row is missing an id.`);
            return;
        }

        setDeleteCandidate(sport);
    };

    const closeDeleteConfirmation = () => {
        if (deletingSportId) return;
        setDeleteCandidate(null);
    };

    const confirmDeleteSport = async () => {
        const sport = deleteCandidate;
        const sportId = sport?._id;
        const sportCode = normalizeSport(sport?.sport);

        if (!sportId) {
            setError(`Unable to delete ${sportCode || 'sport'} because the row is missing an id.`);
            setDeleteCandidate(null);
            return;
        }

        setDeletingSportId(sportId);
        setError('');
        setStatus('');
        try {
            const response = await deleteAdminSport(sportId);
            const data = response.data || {};
            if (!data.success) {
                if (String(data.error || '').toLowerCase().includes('not found')) {
                    setStatus(`${sportCode || 'Sport'} was already deleted. Refreshed the sports list.`);
                    fetchSports();
                    setDeleteCandidate(null);
                    return;
                }
                setError(data.error || `Unable to delete ${sportCode || 'sport'}.`);
                return;
            }

            setSports((prevSports) => prevSports.filter((row) => row._id !== data.sportId));
            setStatus(`${sportCode || 'Sport'} deleted.`);
            setDeleteCandidate(null);
        } catch (err) {
            if (err.response?.status === 404) {
                setStatus(`${sportCode || 'Sport'} was already deleted. Refreshed the sports list.`);
                fetchSports();
                setDeleteCandidate(null);
            } else if (err.response?.status === 401) {
                setError('Admin key rejected. Check the key and try again.');
            } else {
                setError(err.response?.data?.error || err.message || `Unable to delete ${sportCode || 'sport'}.`);
            }
        } finally {
            setDeletingSportId('');
        }
    };

    const renderTable = () => {
        if (loading && sports.length === 0) {
            return (
                <div className={globalStyles.loadingWrapper}>
                    <SmallLoadingAlt />
                </div>
            );
        }

        if (sports.length === 0) {
            return (
                <div className={globalStyles.emptyState}>
                    No configured sports returned.
                </div>
            );
        }

        return (
            <div className={componentStyles.tableWrap}>
                <table className={componentStyles.adminTable}>
                    <thead>
                        <tr>
                            <th>Sport</th>
                            <th>Name</th>
                            <th>Status</th>
                            <th>Last Updated</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sports.map((sport, index) => {
                            const sportCode = normalizeSport(sport.sport);
                            const isActive = Boolean(sport.is_active);
                            const isSaving = savingSport === sportCode;
                            const isDeleting = deletingSportId === sport._id;
                            return (
                                <tr key={sport._id || `missing-id-${index}`}>
                                    <td className={componentStyles.codeCell} data-label="Sport">{sportCode || '-'}</td>
                                    <td data-label="Name">{sport.sport_name || sportCode || '-'}</td>
                                    <td data-label="Status">
                                        <span className={`${componentStyles.statusPill} ${isActive ? componentStyles.statusActive : componentStyles.statusInactive}`}>
                                            {isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td data-label="Last Updated">{formatDateTime(sport.last_updated)}</td>
                                    <td data-label="Actions">
                                        <div className={componentStyles.rowActions}>
                                            <button
                                                type="button"
                                                className={isActive ? globalStyles.secondaryButton : globalStyles.primaryButton}
                                                onClick={() => applySportUpdate(sportCode, !isActive)}
                                                disabled={isSaving || isDeleting || loading}
                                            >
                                                {isSaving ? 'Saving...' : isActive ? 'Disable' : 'Enable'}
                                            </button>
                                            <button
                                                type="button"
                                                className={`${globalStyles.iconButton} ${componentStyles.dangerButton}`}
                                                onClick={() => openDeleteConfirmation(sport)}
                                                disabled={isSaving || isDeleting || loading || !sport._id}
                                                title={sport._id ? `Delete ${sportCode}` : 'Cannot delete without an id'}
                                            >
                                                <FaTrash />
                                                {isDeleting ? 'Deleting...' : 'Delete'}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderDeleteModal = () => {
        if (!deleteCandidate) return null;

        const sportCode = normalizeSport(deleteCandidate.sport);
        const sportName = deleteCandidate.sport_name || sportCode || 'Sport';
        const isDeleting = deletingSportId === deleteCandidate._id;

        return (
            <div
                className={componentStyles.confirmOverlay}
                onClick={closeDeleteConfirmation}
                role="presentation"
            >
                <div
                    className={componentStyles.confirmModal}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="delete-sport-title"
                    onClick={(event) => event.stopPropagation()}
                >
                    <div className={componentStyles.confirmHeader}>
                        <FaTrash />
                        <h3 id="delete-sport-title">Delete Sport</h3>
                    </div>
                    <div className={componentStyles.confirmBody}>
                        <p>Remove this configured sport from admin controls?</p>
                        <div className={componentStyles.confirmDetails}>
                            <div>
                                <span>Sport</span>
                                <strong>{sportCode || '-'}</strong>
                            </div>
                            <div>
                                <span>Name</span>
                                <strong>{sportName}</strong>
                            </div>
                            <div>
                                <span>ID</span>
                                <strong>{deleteCandidate._id}</strong>
                            </div>
                        </div>
                    </div>
                    <div className={componentStyles.confirmActions}>
                        <button
                            type="button"
                            className={globalStyles.secondaryButton}
                            onClick={closeDeleteConfirmation}
                            disabled={isDeleting}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={`${globalStyles.iconButton} ${componentStyles.dangerButton}`}
                            onClick={confirmDeleteSport}
                            disabled={isDeleting}
                        >
                            <FaTrash />
                            {isDeleting ? 'Deleting...' : 'Delete Sport'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={componentStyles.panelStack}>
            <section className={`${globalStyles.standardSection} ${componentStyles.adminSection}`}>
                <div className={componentStyles.sectionHeaderRow}>
                    <h3 className={globalStyles.sectionHeader}>Admin Sports</h3>
                    <button
                        type="button"
                        className={globalStyles.iconButton}
                        onClick={fetchSports}
                        disabled={loading}
                    >
                        <FaSyncAlt />
                        Refresh
                    </button>
                </div>

                <form className={componentStyles.addSportRow} onSubmit={handleAddSport}>
                    <input
                        className={`${globalStyles.standardInput} ${componentStyles.sportCodeInput}`}
                        value={newSport}
                        onChange={(event) => setNewSport(event.target.value)}
                        placeholder="Sport code"
                        aria-label="Sport code"
                        list="admin-sport-options"
                    />
                    <datalist id="admin-sport-options">
                        {availableQuickAdds.map((sport) => (
                            <option key={sport} value={sport} />
                        ))}
                    </datalist>
                    <button
                        type="submit"
                        className={globalStyles.primaryButton}
                        disabled={!normalizeSport(newSport) || Boolean(savingSport)}
                    >
                        <FaPlus />
                        Add Active
                    </button>
                </form>

                {availableQuickAdds.length > 0 && (
                    <div className={componentStyles.quickAddRow} aria-label="Quick add sports">
                        {availableQuickAdds.map((sport) => (
                            <button
                                key={sport}
                                type="button"
                                className={componentStyles.quickAddButton}
                                onClick={() => applySportUpdate(sport, true)}
                                disabled={Boolean(savingSport)}
                            >
                                {sport}
                            </button>
                        ))}
                    </div>
                )}

                {error && <div className={componentStyles.errorBlock}>{error}</div>}
                {status && <div className={componentStyles.statusBlock}>{status}</div>}
                {renderTable()}
            </section>
            {renderDeleteModal()}
        </div>
    );
};

export default AdminSports;
